import React from "react";
import { motion } from "framer-motion";
import { ReactComponent as PeruIcon } from "../../../assets/countries/peru.svg";
import { ReactComponent as ChileIcon } from "../../../assets/countries/chile.svg";
import { ReactComponent as UKIcon } from "../../../assets/countries/uk.svg";
import { ReactComponent as MexicoIcon } from "../../../assets/countries/mexico.svg";
import { ReactComponent as USAIcon } from "../../../assets/countries/usa.svg";
import { ReactComponent as RussiaIcon } from "../../../assets/countries/russia.svg";
import { ReactComponent as ChinaIcon } from "../../../assets/countries/china.svg";
import { ReactComponent as NetherlandsIcon } from "../../../assets/countries/netherlands.svg";
import { ReactComponent as SpainIcon } from "../../../assets/countries/spain.svg";
import { ReactComponent as KoreaIcon } from "../../../assets/countries/korea.svg";
import { ReactComponent as MoroccoIcon } from "../../../assets/countries/morocco.svg";
import { ReactComponent as ItalyIcon } from "../../../assets/countries/italy.svg";
import { ReactComponent as CanadaIcon } from "../../../assets/countries/canada.svg";
import { ReactComponent as CostaRicaIcon } from "../../../assets/countries/costaRica.svg";
import { ReactComponent as ElSalvadorIcon } from "../../../assets/countries/elSalvador.svg";
import { ReactComponent as BelizeIcon } from "../../../assets/countries/belize.svg";
import { ReactComponent as HondurasIcon } from "../../../assets/countries/honduras.svg";
import { ReactComponent as NicaraguaIcon } from "../../../assets/countries/nicaragua.svg";
import { ReactComponent as PanamaIcon } from "../../../assets/countries/panama.svg";
import { ReactComponent as BahamasIcon } from "../../../assets/countries/bahamas.svg";
import { ReactComponent as CurazaoIcon } from "../../../assets/countries/curazao.svg";
import { ReactComponent as StEustaquioIcon } from "../../../assets/countries/stEustaquio.svg";
import { ReactComponent as TrinidadTobagoIcon } from "../../../assets/countries/trinidadTobago.svg";

import { useTranslation } from "react-i18next"

interface Country {
  id: number;
  name: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  location: string;
}

interface CountryBlockProps {
  country: Country;
  index: number;
}

const CountryBlock: React.FC<CountryBlockProps> = ({ country, index }) => {
  const Icon = country.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.03 }}
      whileHover={{ 
        scale: 1.05,
        transition: { duration: 0.2 }
      }}
      className="group flex flex-col items-center p-4 md:p-6 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 border border-white/20 hover:border-white/30 shadow-sm hover:shadow-lg"
    >
      <div className="relative">
        <Icon className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 fill-white group-hover:fill-white/80 transition-colors duration-300" />
        <div className="absolute inset-0 bg-white/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>
      <p className="mt-3 text-sm md:text-base lg:text-lg font-semibold text-white group-hover:text-white/90 transition-colors duration-300 text-center">
        {country.name}
      </p>
    </motion.div>
  );
};

const Locations: React.FC = () => {
  const { t } = useTranslation();

  const countries: Country[] = [
    // South America
    { id: 1, name: t("locations.peruName"), icon: PeruIcon, location: "Peru" },
    { id: 2, name: t("locations.chileName"), icon: ChileIcon, location: "Chile" },

    // North America & Central America
    { id: 3, name: t("locations.mexicoName"), icon: MexicoIcon, location: "Mexico" },
    { id: 21, name: t("locations.canadaName"), icon: CanadaIcon, location: "Canada" },
    { id: 13, name: t("locations.belizeName"), icon: BelizeIcon, location: "Belize" },
    { id: 14, name: t("locations.hondurasName"), icon: HondurasIcon, location: "Honduras" },
    { id: 15, name: t("locations.nicaraguaName"), icon: NicaraguaIcon, location: "Nicaragua" },
    { id: 16, name: t("locations.panamaName"), icon: PanamaIcon, location: "Panama" },
    { id: 22, name: t("locations.costaricaName"), icon: CostaRicaIcon, location: "Costa Rica" },
    { id: 23, name: t("locations.elsalvadorName"), icon: ElSalvadorIcon, location: "El Salvador" },

    // Caribbean
    { id: 17, name: t("locations.bahamasName"), icon: BahamasIcon, location: "Bahamas" },
    { id: 18, name: t("locations.curazaoName"), icon: CurazaoIcon, location: "Curazao" },
    { id: 19, name: t("locations.steustaquioName"), icon: StEustaquioIcon, location: "St. Eustaquio" },
    { id: 20, name: t("locations.trinidadTobagoName"), icon: TrinidadTobagoIcon, location: "Trinidad & Tobago" },

    // Europe
    { id: 4, name: t("locations.ukName"), icon: UKIcon, location: "UK" },
    { id: 7, name: t("locations.netherlandsName"), icon: NetherlandsIcon, location: "Netherlands" },
    { id: 8, name: t("locations.italyName"), icon: ItalyIcon, location: "Italy" },
    { id: 9, name: t("locations.spainName"), icon: SpainIcon, location: "Spain" },

    // Africa
    { id: 10, name: t("locations.moroccoName"), icon: MoroccoIcon, location: "Morocco" },

    // Asia
    { id: 11, name: t("locations.chinaName"), icon: ChinaIcon, location: "China" },
    { id: 12, name: t("locations.koreaName"), icon: KoreaIcon, location: "Korea" },

    // Others
    { id: 5, name: t("locations.usaName"), icon: USAIcon, location: "USA" },
    { id: 6, name: t("locations.russiaName"), icon: RussiaIcon, location: "Russia" }
  ];

  return (
    <div className="py-16 md:py-24 px-4 bg-gradient-to-br from-[#f84831] via-[#ff6b54] to-[#f84831]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">
            {t("locations.title")}
          </h2>
          <div className="mx-auto h-1 w-16 md:w-24 bg-white mb-6 rounded-full"></div>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t("locations.description")}
          </p>
        </motion.div>

        {/* Countries Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
          {countries.map((country, index) => (
            <CountryBlock key={country.id} country={country} index={index} />
          ))}
        </div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-12 md:mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-lg text-white/80 mb-6">
            {t('locations.callToAction')}
          </p>
          <button className="bg-white hover:bg-gray-100 text-[#f84831] px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
            {t('locations.contactExpansion')}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Locations;
